import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

type PlanType = 'starter' | 'professional' | 'growth';

interface StripeCheckoutProps {
  planType: PlanType;
  onSuccess?: (sessionId: string) => void;
  onError?: (error: string) => void;
  buttonText?: string;
  disabled?: boolean;
  className?: string;
}

export const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  planType,
  onSuccess,
  onError,
  buttonText = 'Subscribe',
  disabled = false,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    try {
      setIsLoading(true);

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Authenticated request — planType only, no amount
      const response = await axios.post('/api/payment/checkout-session', { planType });

      const { id } = response.data.data;

      const { error } = await stripe.redirectToCheckout({ sessionId: id });

      if (error) {
        throw new Error(error.message);
      }

      onSuccess?.(id);
    } catch (error) {
      console.error('Checkout error:', error);
      const message = error instanceof Error ? error.message : 'Payment failed';
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleCheckout}
      disabled={disabled || isLoading}
      className={className || `
        w-full px-6 py-3 text-white font-semibold rounded-full transition-colors
        ${disabled || isLoading
          ? 'bg-zinc-400 cursor-not-allowed'
          : 'bg-primary hover:bg-ink-soft active:bg-green-900'
        }
      `}
    >
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          Processing...
        </div>
      ) : (
        buttonText
      )}
    </button>
  );
};

export default StripeCheckout;
